import React, { useState, useEffect } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Moon, Sun } from 'lucide-react';
import { Button } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import { customToast } from '../utils/customToast';
import SuspensionModal from '../components/modals/SuspensionModal';

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suspensionModal, setSuspensionModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'suspended' | 'expired' | 'trial_expired';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'suspended'
  });
  
  const { login, user, isLoading } = useAuth();
  const { getSetting } = useSettings();
  const { theme, toggleTheme } = useTheme();

  console.log('🔄 Login component rendering - user:', user, 'isLoading:', isLoading);

  // Pre-fill demo credentials if demo parameter is present
  useEffect(() => {
    if (searchParams.get('demo') === 'true') {
      setFormData({
        email: 'abu.pos',
        password: 'demo321'
      });
      setShowPassword(true); // Show password for demo
    }
  }, [searchParams]);

  // Check for suspension errors from session storage (auto-logout scenarios)
  useEffect(() => {
    const suspensionError = sessionStorage.getItem('shop_suspension_error');
    if (suspensionError) {
      try {
        const errorData = JSON.parse(suspensionError);
        const { shop_status, message } = errorData;
        
        let modalType: 'suspended' | 'expired' | 'trial_expired' = 'suspended';
        let title = 'Access Denied';
        
        if (message.toLowerCase().includes('trial') && message.toLowerCase().includes('expired')) {
          modalType = 'trial_expired';
          title = 'Trial Period Ended';
        } else if (shop_status === 'suspended') {
          modalType = 'suspended';
          title = 'Account Suspended';
        } else if (shop_status === 'expired') {
          modalType = 'expired';
          title = 'Subscription Expired';
        }
        
        setSuspensionModal({
          isOpen: true,
          title,
          message: message || 'Your access has been revoked.',
          type: modalType
        });
        
        // Clear the error from session storage
        sessionStorage.removeItem('shop_suspension_error');
      } catch (err) {
        console.error('Error parsing suspension error:', err);
        sessionStorage.removeItem('shop_suspension_error');
      }
    }
  }, []);

  // Show loading spinner while auth is initializing
  if (isLoading) {
    console.log('⏳ Auth is loading, showing spinner');
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if already logged in - based on role
  if (user) {
    console.log('🔀 Login: User already logged in, redirecting...', user);
    const redirectPath = user.role === 'super_admin' ? '/admin' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  console.log('📝 Showing login form');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔵 FORM SUBMITTED - Starting login process');
    console.log('📧 Form data:', { email: formData.email, password: '***' });
    setLoading(true);

    try {
      console.log('🔑 Attempting login with:', { email: formData.email });
      const success = await login(formData);
      console.log('✅ Login result:', success);
      console.log('📦 LocalStorage after login - token:', localStorage.getItem('auth_token') ? 'EXISTS' : 'MISSING');
      console.log('📦 LocalStorage after login - user:', localStorage.getItem('user_data') ? 'EXISTS' : 'MISSING');
      
      if (!success) {
        console.log('❌ Login failed');
        customToast.error('Invalid email or password. Please check your credentials and try again.');
      } else {
        console.log('✅ Login successful, should redirect to dashboard');
        customToast.success('Login successful! Redirecting to dashboard...');
      }
    } catch (err: any) {
      console.error('🔴 Login error:', err);
      
      // Check if it's a 403 error (shop suspended/expired)
      if (err.response?.status === 403) {
        const errorData = err.response.data;
        const message = errorData.message;
        const shopStatus = errorData.shop_status;
        
        console.log('🚫 403 Error - Shop Status:', shopStatus, 'Message:', message);
        
        // Determine modal type based on shop status and message
        let modalType: 'suspended' | 'expired' | 'trial_expired' = 'suspended';
        let title = 'Access Denied';
        
        // Check if trial expired (message contains "trial" keyword)
        if (message.toLowerCase().includes('trial') && message.toLowerCase().includes('expired')) {
          modalType = 'trial_expired';
          title = 'Trial Period Ended';
        } else if (shopStatus === 'suspended') {
          modalType = 'suspended';
          title = 'Account Suspended';
        } else if (shopStatus === 'expired') {
          modalType = 'expired';
          title = 'Subscription Expired';
        }
        
        setSuspensionModal({
          isOpen: true,
          title,
          message: message || 'Access denied. Please contact administrator.',
          type: modalType
        });
      } else {
        customToast.error('Invalid email or password. Please try again.');
      }
    } finally {
      setLoading(false);
      console.log('🏁 Login process complete, loading set to false');
    }
  };

  return (
    <div className={`min-h-screen w-full overflow-y-auto overflow-x-hidden transition-colors duration-300 ${
      theme === 'dark' 
        ? 'bg-slate-900' 
        : 'bg-white'
    }`}>
      {/* Theme Toggle - Top Right */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 z-50 p-2 sm:p-3 rounded-xl shadow-lg transition-all duration-300 ${
          theme === 'dark'
            ? 'bg-slate-800 hover:bg-slate-700 text-yellow-400'
            : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
        }`}
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? <Sun size={18} className="sm:w-5 sm:h-5" /> : <Moon size={18} className="sm:w-5 sm:h-5" />}
      </button>
      
      <div className="min-h-screen flex items-center justify-center py-6 sm:py-8 lg:py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-800'
            }`}>
              {getSetting('site_name') || 'NextGen - Shop'}
            </h1>
            <p className={`text-sm sm:text-base font-medium ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {getSetting('site_description') || 'Management System & POS'}
            </p>
          </div>

          {/* Login Card */}
          <div className={`rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl border p-4 sm:p-6 lg:p-8 relative overflow-hidden ${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}>
            {/* Top border accent */}
            <div className="absolute top-0 left-0 w-full h-1 bg-indigo-600"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-center mb-4 sm:mb-6">
                <h2 className={`text-lg sm:text-xl lg:text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-800'
                }`}>
                  Welcome back!
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label htmlFor="email" className={`block text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Email or Username
                    </label>
                    <input
                      id="email"
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 rounded-lg sm:rounded-xl font-medium text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all duration-300 ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Enter your email or username"
                      required
                      autoComplete="username"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className={`block text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 sm:pr-12 border-2 rounded-lg sm:rounded-xl font-medium text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-all duration-300 ${
                          theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                            : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                        }`}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 transition-colors duration-200 p-1 focus:outline-none ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-gray-300'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Forgot Password Link */}
                <div className="flex items-center justify-end pt-1">
                  <Link 
                    to="/forgot-password" 
                    className="text-xs sm:text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors duration-200"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={loading}
                  loadingText="Signing in..."
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 sm:py-3 lg:py-3.5 rounded-lg sm:rounded-xl shadow-lg transform hover:scale-[1.02] transition-all duration-300 border-0 text-sm sm:text-base mt-2"
                >
                  Sign In to Dashboard
                </Button>
              </form>
            </div>
          </div>
          
          {/* Additional Info */}
          <p className={`text-center mt-3 sm:mt-4 text-xs sm:text-sm ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
          }`}>
            Need help? Contact your system administrator
          </p>
          
          {/* Footer */}
          <div className="text-center mt-4 sm:mt-6 px-4">
            <p className={`text-xs sm:text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              © {new Date().getFullYear()} {getSetting('site_name') || 'NextGen - Shop'}. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Suspension Modal */}
      <SuspensionModal
        isOpen={suspensionModal.isOpen}
        onClose={() => setSuspensionModal({ ...suspensionModal, isOpen: false })}
        title={suspensionModal.title}
        message={suspensionModal.message}
        type={suspensionModal.type}
      />
    </div>
  );
};

export default Login;